import {
  exchangedSideOf,
  transactionTypeOfExchange,
} from "$routes/_lib/billing-v2/currency-exchange";
import { INSURER } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/InsurerAccordion/formConstants";
import dayjs from "dayjs";
import { TransactionType } from "elephant-web/gql";
import { Align, Style } from "escpos-buffer";
import { getBorderCharacters, table } from "table";
import { getLogo } from "./print-logo";

async function printClinicDetails(printer, invoice, intl) {
  const { patientType, facility } = invoice;
  const [address] = facility.addresses;

  await printer.writeln(
    facility.name,
    Style.DoubleHeight | Style.Bold,
    Align.Center,
  );
  await printer.writeln(
    [address.street, address.subCounty, address.state].join("\n"),
    null,
    Align.Center,
  );
  await printer.writeln(
    intl.formatMessage(
      {
        id: "invoices.thermalReceipt.receiptType",
        defaultMessage: "{patientType} payment receipt",
      },
      { patientType },
    ),
    Style.DoubleHeight | Style.Bold,
    Align.Center,
  );
  await printer.writeln("");
}

async function printHeader(printer, invoice, intl) {
  await printClinicDetails(printer, invoice, intl);
  const { name } = invoice?.patient?.person ?? {};
  await printer.writeln(
    intl.formatMessage(
      {
        id: "invoices.thermalReceipt.patientName",
        defaultMessage: "Patient name: {firstname} {lastname}",
      },
      { firstname: name?.firstname ?? "", lastname: name?.lastname ?? "" },
    ),
  );
  const cardId = invoice.patient.cardId || invoice.patient?.passport?.uid;
  if (cardId) {
    await printer.writeln(
      intl.formatMessage(
        {
          id: "invoices.thermalReceipt.patientId",
          defaultMessage: "Patient ID: {cardId}",
        },
        { cardId },
      ),
    );
  }
  await printer.writeln("");
  await printer.writeln(
    intl.formatMessage(
      {
        id: "invoices.thermalReceipt.invoiceNumber",
        defaultMessage: "Invoice number: {invoiceNumber}",
      },
      { invoiceNumber: invoice.invoiceNumber },
    ),
    Style.Bold,
  );
  await printer.writeln("");
}

async function printItems(printer, invoice, currencyFormatter, penceToPounds) {
  const { lineItems } = invoice;
  const { columns } = printer;

  const tableData = lineItems
    .filter((lineItem) => !lineItem.disabled)
    .map((lineItem) => {
      const { priceListItem, quantity, cost } = lineItem;
      return [
        priceListItem.name,
        `x${quantity}`,
        currencyFormatter.format(penceToPounds(cost)),
      ];
    });

  const maxLenCurrency = tableData.reduce(
    (max, [, , formattedAmount]) => Math.max(max, formattedAmount.length),
    0,
  );
  const paddingRight = 1;
  const qtyWidth = 5;
  const nameWidth = columns - qtyWidth - maxLenCurrency - paddingRight * 3;

  const careEventTable = table(tableData, {
    border: getBorderCharacters("void"),
    columnDefault: {
      paddingLeft: 0,
      paddingRight,
      width: columns / 3,
    },
    drawHorizontalLine: false,
    columns: {
      0: {
        width: nameWidth,
      },
      1: {
        width: qtyWidth,
        alignment: "right",
      },
      2: {
        width: maxLenCurrency,
        alignment: "right",
      },
    },
  });
  await printer.write(careEventTable);
  await printer.writeln(
    Array(printer.columns).fill("_").join(""),
    Style.DoubleHeight | Style.Bold | Style.Underline,
    Align.Center,
  );
  await printer.writeln("");
}

async function printTotal(
  printer,
  invoice,
  currencyFormatter,
  penceToPounds,
  intl,
) {
  const { columns } = printer;
  const rightText = currencyFormatter.format(
    penceToPounds(invoice.balance.paid),
  );
  const leftText = intl.formatMessage({
    id: "invoices.thermalReceipt.paid",
    defaultMessage: "Paid",
  });
  const spaceCount = columns - leftText.length - rightText.length;
  const spaces = Array(spaceCount).fill(" ").join("");

  await printer.writeln(
    `${leftText}${spaces}${rightText}`,
    Style.DoubleHeight | Style.Bold,
  );
  await printer.writeln("");
  const printPayerTotal = async (name, amount) => {
    const formattedAmount = currencyFormatter.format(penceToPounds(amount));
    const totalSpaces = columns - name.length - formattedAmount.length;
    const serialisedSpaces = Array(totalSpaces).fill(" ").join("");
    await printer.writeln(
      `${name}${serialisedSpaces}${formattedAmount}`,
      Style.DoubleHeight | Style.Bold,
    );
  };
  if (invoice?.payers?.length > 0) {
    for (const p of invoice.payers) {
      const name = p?.insuranceDetails?.name || p.name;
      const amount = p.balance.paid;
      await printPayerTotal(name, amount);
    }
  }

  const totalWaived = invoice.transactions
    .filter((t) => t.payer.type === TransactionType.Waiver)
    .reduce((prevVal, curr) => prevVal + curr.amount.value, 0);

  if (totalWaived) {
    await printPayerTotal("Waived", totalWaived);
  }
}

async function printCurrencyExchange(
  printer,
  tx,
  invoiceCurrency,
  currencyExchangeFormat,
  intl,
) {
  if (!tx.currencyExchange || !currencyExchangeFormat) return;
  const { exchangeRate } = tx.currencyExchange;
  const type = transactionTypeOfExchange(tx.currencyExchange, invoiceCurrency);
  const exchanged = exchangedSideOf(tx.currencyExchange, type);
  const values = {
    currency: exchanged.currency,
    amount: currencyExchangeFormat.formatMoney(
      exchanged.currency,
      exchanged.value,
    ),
  };
  await printer.writeln(
    type === TransactionType.Refund
      ? intl.formatMessage(
          {
            id: "invoices.thermalReceipt.currencyExchange.refunded",
            defaultMessage: "Refunded in {currency}: {amount}",
          },
          values,
        )
      : intl.formatMessage(
          {
            id: "invoices.thermalReceipt.currencyExchange.paid",
            defaultMessage: "Paid in {currency}: {amount}",
          },
          values,
        ),
  );
  await printer.writeln(
    intl.formatMessage(
      {
        id: "invoices.thermalReceipt.currencyExchange.rate",
        defaultMessage: "Exchange rate: {rate} ({date})",
      },
      {
        rate: currencyExchangeFormat.formatRate(exchangeRate),
        date: dayjs(exchangeRate.effectiveDate).format("DD/MM/YYYY"),
      },
    ),
  );
}

async function printTransactions(
  printer,
  transactions,
  currencyFormatter,
  penceToPounds,
  intl,
  invoiceCurrency,
  currencyExchangeFormat,
) {
  const filteredTransactions = transactions.filter(
    (t) => t.payer.type !== INSURER,
  );
  if (filteredTransactions.length > 1) {
    await printer.writeln(
      intl.formatMessage(
        {
          id: "invoices.thermalReceipt.transactions",
          defaultMessage: "{length} Transactions",
        },
        { length: filteredTransactions.length },
      ),
      Style.Bold,
    );
  }

  for (const tx of filteredTransactions) {
    switch (tx.type) {
      case "PAYMENT":
        await printer.writeln(
          intl.formatMessage(
            {
              id: "invoices.thermalReceipt.paymentType",
              defaultMessage: "Payment type: {type}",
            },
            { type: tx?.payment?.paymentMethod },
          ),
        );
        break;
      case "REFUND":
        await printer.writeln(
          intl.formatMessage(
            {
              id: "invoices.thermalReceipt.paymentType.refund",
              defaultMessage: "Refund type: {type}",
            },
            { type: tx?.payment?.paymentMethod || tx?.refund?.paymentMethod },
          ),
        );
        break;
      case "WAIVER":
        await printer.writeln(
          intl.formatMessage({
            id: "invoices.thermalReceipt.paymentType.wavier",
            defaultMessage: "Waiver:",
          }),
        );
        break;
      case "EXEMPT":
        await printer.writeln(
          intl.formatMessage({
            id: "invoices.thermalReceipt.paymentType.exempt",
            defaultMessage: "Exempt:",
          }),
        );
        break;
      default:
        break;
    }
    await printCurrencyExchange(
      printer,
      tx,
      invoiceCurrency,
      currencyExchangeFormat,
      intl,
    );
    if (tx?.payment?.authCode || tx?.refund?.authCode) {
      await printer.writeln(
        intl.formatMessage(
          {
            id: "invoices.thermalReceipt.authCode",
            defaultMessage: "Transaction code: {code}",
          },
          { code: tx?.payment?.authCode || tx?.refund?.authCode },
        ),
      );
    }

    if (filteredTransactions.length > 1) {
      await printer.writeln(
        `${currencyFormatter.format(penceToPounds(tx?.amount.value || 0))}`,
      );
    }

    if (tx.actionedBy) {
      await printer.writeln(
        intl.formatMessage(
          {
            id: "invoices.thermalReceipt.actionedBy",
            defaultMessage: "Served by: {firstname} {lastname}",
          },
          {
            firstname: tx.actionedBy.person.firstname ?? "",
            lastname: tx.actionedBy.person.lastname ?? "",
          },
        ),
      );
    }

    await printer.writeln();
  }
}

async function printFooter(
  printer,
  invoice,
  currencyFormatter,
  penceToPounds,
  intl,
  currencyExchangeFormat,
) {
  const { created } = invoice;
  await printTransactions(
    printer,
    invoice.transactions,
    currencyFormatter,
    penceToPounds,
    intl,
    invoice.balance?.currency,
    currencyExchangeFormat,
  );

  const timestamp = dayjs(created).format("DD/MM/YYYY HH:mm");
  await printer.writeln(
    intl.formatMessage(
      {
        id: "invoices.thermalReceipt.on",
        defaultMessage: "On: {timestamp}",
      },
      { timestamp },
    ),
  );

  await printer.writeln("");
  await printer.setAlignment(Align.Center);
  await printer.draw(await getLogo());
  await printer.setAlignment(Align.Left);
}

export async function printV2Receipt({
  printer,
  invoice,
  currencyFormatter,
  penceToPounds,
  intl,
  currencyExchangeFormat = undefined,
}) {
  await printHeader(printer, invoice, intl);
  await printItems(printer, invoice, currencyFormatter, penceToPounds);
  await printTotal(printer, invoice, currencyFormatter, penceToPounds, intl);
  await printFooter(
    printer,
    invoice,
    currencyFormatter,
    penceToPounds,
    intl,
    currencyExchangeFormat,
  );
  await printer.feed(5);
  await printer.cutter();
}
